<template>
  <div v-if="!result" class="calculation-steps">
    <h2 class="h4">
      How to Calculate Sample Standard Deviation: A Step-by-Step Guide
    </h2>
    <p class="bottom-margins">
      Statistical analysis software typically calculates standard deviation automatically. You can also quickly obtain it if you select Sample in our Standard Deviation Calculator. But computing it manually can aid in understanding how the formula works.
    </p>
    <p>
      To calculate the <span class="colored-texts">sample standard deviation</span> of a dataset, you can follow these steps:
    </p>
    <ol>
      <li>
        <p class="top-margins">
          Calculate the sample’s mean value:
        </p>
        <p>
          \[\bar{X}=\frac{\sum_{i=1}^{n}X_i}{n}\]
        </p>
      </li>
      <li>
        <p>Calculate the deviations from the sample mean:</p>
        <p>
          \[(X_i-\bar{X})\]
        </p>
      </li>
      <li>
        <p>Square each deviation from the sample mean:</p>
        <p>
          \[{(X_i-\bar{X})}^2\]
        </p>
      </li>
      <li>
        <p>Find the sum of the squared deviations:</p>
        <p>
          \[\sum_{i=1}^{n}{(X_i-\bar{X})}^2\]
        </p>
      </li>
      <li>
        <p>Divide the sum of squared deviations by n – 1 to obtain the variance (<span class="inline-formulas">\[s^2\]</span>):</p>
        <p>
          \[s^2=\sum_{i=1}^{n}\frac{(X_i-\bar{X})^2}{n-1}\]
        </p>
      </li>
      <li>
        <p>Take the square root of the sum of the squared deviations to calculate the sample standard deviation:</p>
        <p>
          \[s=\sqrt{\sum_{i=1}^{n}\frac{(X_i-\bar{X})^2}{n-1}}\]
        </p>
      </li>
    </ol>

    <h2 class="h4 additional-h2">
      How to Calculate Population Standard Deviation: A Step-by-Step Guide
    </h2>
    <div class="bottom-margins">
      <p>If, you’re looking for a quick calculation, select Population in our Standard Deviation Calculator to obtain the value. Yet, it’s useful to know how to do it by hand as well. </p>
      <p class="spacing-top">
        To find the population standard deviation manually, follow these steps:
      </p>
    </div>
    <ol>
      <li>
        <p>Calculate the population’s mean value:</p>
        <p>
          \[\mu=\frac{\sum_{i=1}^{n}X_i}{n}\]
        </p>
      </li>
      <li>
        <p>Calculate the deviations from the population mean:</p>
        <p>
          \[(X_i-\mu)\]
        </p>
      </li>
      <li>
        <p>Square each deviation from the population mean:</p>
        <p>
          \[{(X_i-\mu)}^2\]
        </p>
      </li>
      <li>
        <p>Find the sum of the squared deviations:</p>
        <p>
          \[\sum_{i=1}^{n}{(X_i-\mu)}^2\]
        </p>
      </li>
      <li>
        <p>Divide the sum of squared deviations by n to obtain the population variance (<span class="inline-formulas">\[\sigma^2\]</span>):</p>
        <p>
          \[\sigma^2=\sum_{i=1}^{n}\frac{(X_i-\mu)^2}{n}\]
        </p>
      </li>
      <li>
        <p>Take the square root of the sum of the squared deviations to calculate the population standard deviation:</p>
        <p class="default-last-formula">
          \[\sigma=\sqrt{\sum_{i=1}^{n}\frac{(X_i-\mu)^2}{n}}\]
        </p>
      </li>
    </ol>
  </div>

  <div v-else :key="result" class="calculation-steps result">
    <h2 class="h4">
      Calculation Steps:
    </h2>
    <p class="bottom-margins">
      Follow these steps to obtain the {{ calculationData.method }} standard deviation of your dataset:
    </p>
    <ol>
      <li>
        <p>Calculate the {{ calculationData.method }}’s mean value:</p>
        <p v-if="calculationData.method === 'sample'" class="formulas">
          \[\bar{X}=\frac{\sum_{i=1}^{n}X_i}{n}=\frac{ {{ additionString }} }{ {{ calculationData.size }} }={{ calculationData.mean }}\]
        </p>
        <p v-else class="formulas">
          \[\mu=\frac{\sum_{i=1}^{n}X_i}{n}=\frac{ {{ additionString }} }{ {{ calculationData.size }} }={{ calculationData.mean }}\]
        </p>
      </li>
      <li>
        <p class="top-margins">
          Calculate the deviations from the {{ calculationData.method }} mean. For example, for the first variable, we have the following:
        </p>
        <p v-if="calculationData.method === 'sample'" class="formulas">
          \[(X_i-\bar{X})={{ calculationData.rawDataMeanArray[0] }}-{{ calculationData.mean }}={{ calculationData.deviationsFromMeanFirstValue }}\]
        </p>
        <p v-else class="formulas">
          \[(X_i-\mu)={{ calculationData.rawDataMeanArray[0] }}-{{ calculationData.mean }}={{ calculationData.deviationsFromMeanFirstValue }}\]
        </p>
      </li>
      <li>
        <p class="top-margins">
          Square each deviation from the {{ calculationData.method }} mean. For example, for the first variable, we have the following:
        </p>
        <p v-if="calculationData.method === 'sample'" class="formulas">
          \[{(X_i-\bar{X})}^2={ {{ calculationData.deviationsFromMeanFirstValue }} }^2={{ calculationData.deviationsFromMeanSquaredFirstValue }}\]
        </p>
        <p v-else class="formulas">
          \[{(X_i-\mu)}^2={ {{ calculationData.deviationsFromMeanFirstValue }} }^2={{ calculationData.deviationsFromMeanSquaredFirstValue }}\]
        </p>
      </li>
      <li>
        <p class="top-margins">
          Find the sum of the squared deviations from the mean:
        </p>
        <p v-if="calculationData.method === 'sample'" class="formulas">
          \[\sum_{i=1}^{n}{\left(X_i-\bar{X}\right)^2=\sum_{i=1}^{n}{ {{ sqrString }} }={{ calculationData.sumOfSquaredDeviations }}}\]
        </p>
        <p v-else class="formulas">
          \[\sum_{i=1}^{n}{\left(X_i-\mu\right)^2=\sum_{i=1}^{n}{ {{ sqrString }} }={{ calculationData.sumOfSquaredDeviations }}}\]
        </p>
      </li>
      <li v-if="calculationData.method === 'sample'" class="top-margins">
        <p>Divide the sum of squared deviations from the mean by n – 1 to obtain the variance (<span class="inline-formulas">\[s^2\]</span>).</p>
        <p>
          \[s^2=\sum_{i=1}^{n}\frac{(X_i-\bar{X})^2}{n-1}=\frac{ {{ calculationData.sumOfSquaredDeviations }} }{ {{ calculationData.size }}-1}={{ calculationData.variance }}\]
        </p>
      </li>
      <li v-else class="top-margins">
        <p>Divide the sum of squared deviations from the mean by n to obtain the population variance (<span class="inline-formulas">\[\sigma^2\]</span>).</p>
        <p class="formulas">
          \[\sigma^2=\sum_{i=1}^{n}\frac{(X_i-\mu)^2}{n}=\frac{ {{ calculationData.sumOfSquaredDeviations }} }{ {{ calculationData.size }} }={{ calculationData.variance }}\]
        </p>
      </li>
      <li>
        <p class="top-margins">
          Take the square root of the sum of the squared deviations to calculate the {{ calculationData.method }} standard deviation:
        </p>
        <p v-if="calculationData.method === 'sample'" class="formulas">
          \[s=\sqrt{\sum_{i=1}^{n}\frac{(X_i-\bar{X})^2}{n-1}}=\sqrt{ {{ calculationData.variance }} }={{ calculationData.standardDeviation }}\]
        </p>
        <p v-else class="formulas">
          \[\sigma=\sqrt{\sum_{i=1}^{n}\frac{(X_i-\mu)^2}{n}}=\sqrt{ {{ calculationData.variance }} }={{ calculationData.standardDeviation }}\]
        </p>
      </li>
    </ol>
    <p class="small-margins">
      The {{ calculationData.method }} standard deviation is equal to <span class="colored-texts">{{ calculationData.standardDeviation }}</span>
    </p>

    <div v-if="calculationData.method === 'sample'">
      <div class="pythonRCodes python">
        <span class="img-text"><img alt="Python icon" src="../../../../assets/images/icon-python-calc.png"> <h4 class="h6">Your Result in Python:</h4></span>
        <p class="codes-info">
          The following Python code should provide {{ calculationData.method }} standard deviation using the input data you entered.
        </p>
        <div class="codes-container">
          <div class="codes">
            <pre
              class="language-python"
            ><code id="python-sample"># Import relevant library

import statistics

# Calculate the sample standard deviation

data = <span class="no-wrap">{{ inputToNumbers }}</span>

std_dev = statistics.stdev(data)

print("Sample Standard Deviation:", std_dev)</code></pre>
          </div>
        </div>
        <button class="btn-link-accent" :class="{ copied: copyCodeOrCopiedPython === 'Copied' }" @click="copyCode('python-sample')">
          {{ copyCodeOrCopiedPython }}
        </button>
      </div>
      <div class="pythonRCodes">
        <span class="img-text"><img alt="Icon for R language" src="../../../../assets/images/icon-r-calc.png"> <h4 class="h6">Your Result in R:</h4></span>
        <p class="codes-info">
          The following R code should provide {{ calculationData.method }} standard deviation using the input data you entered.
        </p>
        <div class="codes-container">
          <div class="codes">
            <pre
              class="language-r"
            ><code id="r-sample"># Create vector of data

data &lt;- c(<span class="no-wrap">{{ arrayToString }}</span>)

# Calculate the sample standard deviation

sample_stdev &lt;- sd(data)

cat("Sample Standard Deviation:", sample_stdev, "\n")</code></pre>
          </div>
        </div>
        <button class="btn-link-accent" :class="{ copied: copyCodeOrCopiedR === 'Copied' }" @click="copyCode('r-sample')">
          {{ copyCodeOrCopiedR }}
        </button>
      </div>
    </div>
    <div v-else>
      <div class="pythonRCodes python">
        <span class="img-text"><img alt="Python icon" src="../../../../assets/images/icon-python-calc.png"> <h4 class="h6">Your Result in Python:</h4></span>
        <p class="codes-info">
          The following Python code should provide {{ calculationData.method }} standard deviation using the input data you entered.
        </p>
        <div class="codes-container">
          <div class="codes">
            <pre
              class="language-python"
            ><code id="python-population"># Import relevant library

import statistics

# Calculate the population standard deviation

data = <span class="no-wrap">{{ inputToNumbers }}</span>

std_dev = statistics.pstdev(data)

print("Population Standard Deviation:", std_dev)</code></pre>
          </div>
        </div>
        <button class="btn-link-accent" :class="{ copied: copyCodeOrCopiedPython === 'Copied' }" @click="copyCode('python-population')">
          {{ copyCodeOrCopiedPython }}
        </button>
      </div>
      <div class="pythonRCodes">
        <span class="img-text"><img alt="Icon for R language" src="../../../../assets/images/icon-r-calc.png"> <h4 class="h6">Your Result in R:</h4></span>
        <p class="codes-info">
          The following R code should provide {{ calculationData.method }} standard deviation using the input data you entered.
        </p>
        <div class="codes-container">
          <div class="codes">
            <pre
              class="language-r"
            ><code id="r-population"># Create vector of data

data &lt;- c(<span class="no-wrap">{{ arrayToString }}</span>)

# Calculate the standard deviation

pop_std_dev &lt;- sqrt(var(data) * (length(data) - 1) / length(data))

print(paste("Population Standard Deviation:", pop_std_dev))</code></pre>
          </div>
        </div>
        <button class="btn-link-accent" :class="{ copied: copyCodeOrCopiedR === 'Copied' }" @click="copyCode('r-population')">
          {{ copyCodeOrCopiedR }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';

export default {
  data() {
    return {
      copyCodeOrCopiedPython: 'Copy Code',
      copyCodeOrCopiedR: 'Copy Code',
    };
  },
  computed: {
    ...mapState('calculators', ['result', 'calculationData']),
    additionString() {
      const { length } = this.calculationData.rawDataMeanArray;
      const rawDataMeanArray = this.calculationData.rawDataMeanArray.map((num, i) => (length - 1 > i ? `${num} + ` : num));
      return rawDataMeanArray.join('');
    },
    sqrString() {
      const { length } = this.calculationData.rawDataMeanArray;
      const rawDataMeanArray = this.calculationData.rawDataMeanArray.map((num, i) => (length - 1 > i ? `(${num} - ${this.calculationData.mean})^2 + ` : `(${num} - ${this.calculationData.mean})^2`));
      return rawDataMeanArray.join('');
    },
    inputToNumbers() {
      return this.calculationData.rawDataMeanArray.map((str) => Number(str));
    },
    arrayToString() {
      return this.calculationData.rawDataMeanArray.join(', ');
    },
  },
  methods: {
    copyCode(id) {
      if (id.split('-')[0] === 'python') {
        this.copyCodeOrCopiedPython = 'Copied';
        setTimeout(() => {
          this.copyCodeOrCopiedPython = 'Copy Code';
        }, 1000);
      } else {
        this.copyCodeOrCopiedR = 'Copied';
        setTimeout(() => {
          this.copyCodeOrCopiedR = 'Copy Code';
        }, 1000);
      }

      const currentText = document.getElementById(id).innerText;
      this.$copy(currentText);
    },
  },
};
</script>

<style lang="scss" scoped>
.calculation-steps {
  p {
    margin: 0;
  }

  .spacing-top {
    margin-top: var(--spacing-20);
  }

  .colored-texts {
    font-weight: 600;
  }

  .pythonRCodes.python {
    margin-top: var(--spacing-80);
  }

  ul {
    padding-left: 0;
  }

  .additional-h2 {
    margin-top: var(--spacing-24);
  }
}

@include bp($bp-mobile) {
  div .calculation-steps {
    padding-top: var(--spacing-40);

    .h4 {
      text-align: left;
    }
  }
}

@include bp($bp-phone) {
  .calculation-steps {
    .additional-h2 {
      margin-top: 64px;
    }
  }
}

@import '../../../../assets/sass/ds/calculators/calculation-steps.scss';
</style>
